import type { ProgramEventListType, ProgramUserRsvp } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, DotSeparator, GroupLineIcon, Row } from 'ui';
import { useEventRsvpLabels } from '../../hooks';

type P = {
  eventType: ProgramEventListType;
  inviteesCount: { going: number; notGoing: number; pending: number };
};

const InviteesCount = ({ eventType, inviteesCount: { going, notGoing, pending } }: P) => {
  const { getCountLabel } = useEventRsvpLabels();
  const { t } = useTranslation();

  const rsvpItems = useMemo(
    (): Array<{ count: number; rsvpType: ProgramUserRsvp }> => [
      { count: going, rsvpType: 'going' },
      { count: pending, rsvpType: 'pending' },
      { count: notGoing, rsvpType: 'not_going' },
    ],
    [going, pending, notGoing],
  );

  const getRsvpItems = useMemo(() => {
    if (eventType === 'reminder') {
      return rsvpItems.filter(({ rsvpType }) => rsvpType === 'going');
    }

    return rsvpItems;
  }, [eventType, rsvpItems]);

  const getEventCountLabel = useCallback(
    ({ count, rsvpType }: { count: number; rsvpType: ProgramUserRsvp }) => {
      if (eventType === 'reminder') {
        return t('{{count}} people', { count });
      }

      return getCountLabel({ count, rsvp: rsvpType });
    },
    [eventType, getCountLabel, t],
  );

  return (
    <Row gutter={['xxs', 0]}>
      <Col flex="0 0 auto">
        <GroupLineIcon size="extra-small" />
      </Col>
      <Col>
        <DotSeparator
          items={getRsvpItems.map(({ count, rsvpType }) => ({
            content: getEventCountLabel({ count, rsvpType }),
            key: rsvpType,
          }))}
        />
      </Col>
    </Row>
  );
};

export default InviteesCount;
